import moment from 'moment';
import getUTCNoonForDate from './getUTCNoonForDate';
import useGlobalVariables from '../hooks/useGlobalVariables';
import testDateLogicRule from './testDateLogicRule';

// if we change this we need to change AMLFieldView.js too
const FIELDS_THAT_ALWAYS_HIDE_SUBFIELDS_FOR_END_USERS = [
  'worldcheck',
  'worldcheck_org'
];
const SUBFIELDS_TO_ALWAYS_HIDE_FOR_END_USERS = ['status', 'date'];

// credas needs to be handled separately
const ADDITIONAL_SUBFIELDS_TO_HIDE_FOR_CREDAS_END_USERS = [
  'loe',
  'addressMatch',
  'loeStatus',
  'checktype',
  'runAgain',
  'title',
  'contactVia',
  'status',
  'date'
];

/**
 * Given lists of input fields and output fields, evaluates a list of rules using a list of values for the fields.
 */
export default function getHiddenFields({
  fields = [],
  rules = [],
  values = {},
  config = {}
}) {
  const results = {
    hidden: [],
    info: {}
  };

  if (!values) {
    values = {};
  }

  rules.forEach(rule => {
    const {
      action: { actionType, parameters },
      condition
    } = rule;

    // only interested in show or hide rules
    if (!['hide', 'show'].includes(actionType)) {
      return;
    }

    const target = parameters && parameters[0] && parameters[0].value;

    if (!target) {
      return;
    }

    if (!fields.find(f => f.internalId === target)) {
      results.info[target] = {
        errors: [
          {
            error: 'target does not exist',
            parameters: { condition }
          }
        ],
        failed: [],
        passed: []
      };
      return;
    }

    results.info[target] = results.info[target] || {
      passed: [],
      failed: [],
      errors: []
    };

    const report = (key, value) => {
      results.info[target][key].push(value);
    };

    let fulfilled = evaluateCondition(
      fields,
      values,
      report,
      config
    )(condition);

    if (
      (actionType === 'show' && !fulfilled) ||
      (actionType === 'hide' && fulfilled)
    ) {
      results.hidden.push(rule.fieldId);
    }
  });

  let isInHiddenSection = false;
  fields.forEach(field => {
    if (field.uiType === 'section_break') {
      isInHiddenSection = results.hidden.includes(field.internalId);
    }

    if (isInHiddenSection && !results.hidden.includes(field.internalId)) {
      results.hidden.push(field.internalId);
    }

    const isASubField = field?.lookupInfo?.subFieldInfo?.parentFieldId;
    const fieldsSubFieldInfo = field?.lookupInfo?.subFieldInfo;

    if (isASubField) {
      if (results.hidden.indexOf(fieldsSubFieldInfo.parentFieldId) !== -1) {
        results.hidden.push(field.internalId);
      } else {
        const parentField = fields.find(
          one => one.internalId === fieldsSubFieldInfo.parentFieldId
        );

        if (
          parentField &&
          FIELDS_THAT_ALWAYS_HIDE_SUBFIELDS_FOR_END_USERS.includes(
            parentField.uiType
          ) &&
          SUBFIELDS_TO_ALWAYS_HIDE_FOR_END_USERS.includes(
            fieldsSubFieldInfo.externalFormFieldId
          )
        ) {
          results.hidden.push(field.internalId);
        } else if (
          !config?.showAdminFields &&
          parentField &&
          parentField.uiType === 'credas' &&
          ADDITIONAL_SUBFIELDS_TO_HIDE_FOR_CREDAS_END_USERS.includes(
            fieldsSubFieldInfo.externalFormFieldId
          )
        ) {
          results.hidden.push(field.internalId);
        }
      }
    }
  });

  return results;
}

const conditionHasInternOpOr = c =>
  c.internOp && c.internOp.toLowerCase() === 'or';

const evaluateCondition =
  // use two-order function so we can use the first order to recurse
  (fields, values, report, config) => condition => {
    let passed;

    // parse if necessary
    if (typeof condition === 'string') {
      try {
        condition = JSON.parse(condition);
      } catch (e) {
        report('errors', {
          error: 'invalid json',
          parameters: { condition }
        });
        return false;
      }
    }

    // recurse
    if (Array.isArray(condition)) {
      // check for OR
      const hasOr = condition.findIndex(conditionHasInternOpOr) >= 0;

      return hasOr
        ? condition.some(evaluateCondition(fields, values, report, config))
        : condition.every(evaluateCondition(fields, values, report, config));
    }

    // support single-key `{ "internOp": "or" }`
    if (
      !('field' in condition) &&
      !('value' in condition) &&
      conditionHasInternOpOr(condition)
    ) {
      return false;
    }

    // do we have a condition to evaluate?
    if (condition.field && condition.op) {
      const {
        field,
        op,
        value: rawValue,
        type,
        intervalUnit,
        intervalCount
      } = condition;
      const value =
        rawValue === 'NOW()' || rawValue === 'now'
          ? getUTCNoonForDate(moment(new Date()))
          : rawValue;
      const id = unwrapFieldId(field);
      const input = fields.find(f => f.internalId === id);

      // did we find an input that matches "field"?
      if (!input && op !== 'IS NULL') {
        report('errors', {
          error: 'field not found',
          parameters: { field: id, condition }
        });
        return false;
      }

      if (!input && config.isSectionAdmin) {
        if (config.reportedId !== id) {
          alert(
            `Error: the user does not have access to a field used by a condition\nfield id: ${id}\ncondition: ${JSON.stringify(
              condition
            )}`
          );
        }
        config.reportedId = id;
        report('errors', {
          error: 'field not found',
          parameters: { field: id, condition }
        });
        return false;
      }

      let userValue = values[id];

      // evaluate the condiion
      switch (input.uiType) {
        case 'date':
          passed = testDateLogicRule(userValue, condition);
          report(passed ? 'passed' : 'failed', condition);
          return passed;
        case 'number':
        case 'score':
        case 'currency':
          // empty field always fails
          if (userValue === '' || userValue === null) {
            report('failed', condition);
            return false;
          }

          switch (op) {
            case '=':
              passed = +userValue === +value;
              break;

            case '>':
              passed = +userValue > +value;
              break;

            case '>=':
              passed = +userValue >= +value;
              break;

            case '<':
              passed = +userValue < +value;
              break;

            case '<=':
              passed = +userValue <= +value;
              break;

            default:
              report('errors', {
                error: 'invalid op',
                parameters: { condition, type: input.uiType, op }
              });
              return false;
          }

          report(passed ? 'passed' : 'failed', condition);
          return passed;

        case 'email_address':
        case 'text':
        case 'long_text':
        case 'multiple_choice':
        case 'lookup':
        case 'user_lookup':
        case 'status':
          userValue = String(userValue || '');

          switch (op) {
            case 'ILIKE':
              if (input.uiType === 'multiple_choice' && value === '||||') {
                passed = userValue === '||||' || !userValue;
              } else {
                passed = ilikeToRegExp(value.trim()).test(userValue);
              }
              break;

            case 'NOT ILIKE':
              if (input.uiType === 'multiple_choice' && value === '||||') {
                passed = userValue !== '||||' && userValue;
              } else {
                passed = !ilikeToRegExp(value.trim()).test(userValue);
              }
              break;

            case '=':
              passed = value.trim() === userValue.trim();
              break;

            case '<>':
              passed = value.trim() !== userValue.trim();
              break;

            default:
              report('errors', {
                error: 'invalid op',
                parameters: { condition, type: input.uiType, op }
              });
              return false;
          }

          report(passed ? 'passed' : 'failed', condition);
          return passed;

        case 'confirmation':
          switch (op) {
            case '=':
              passed = value === String(userValue || 'false');
              break;
            case 'IS NULL':
              passed = !!!userValue;
              break;

            default:
              report('errors', {
                error: 'invalid op',
                parameters: { condition, type: input.uiType, op }
              });
              return false;
          }
          report(passed ? 'passed' : 'failed', condition);
          return passed;

        case 'yes_no':
        case 'dropdown':
          switch (op) {
            case '=':
              passed = value === String(userValue || '');
              break;

            case '<>':
              passed = value !== String(userValue || '');
              break;

            case 'IS NULL':
              passed = userValue === null;
              break;

            case 'IS NOT NULL':
              passed = userValue !== null;
              break;
            case 'ILIKE':
              passed = ilikeToRegExp(value.trim()).test(userValue);
              break;
            default:
              report('errors', {
                error: 'invalid op',
                parameters: { condition, type: input.uiType, op }
              });
              return false;
          }
          report(passed ? 'passed' : 'failed', condition);
          return passed;

        default:
          passed = false;
          break;
      }

      report(passed ? 'passed' : 'failed', condition);
      return passed;
    }

    report('errors', {
      error: 'invalid condition',
      parameters: { condition }
    });

    return false;
  };

export const unwrapFieldId = id =>
  id.includes(`'endUserInput'->'`)
    ? id.replace(/'endUserInput'->'([^']+)'/, '$1')
    : id.indexOf('${') === 0
    ? id.replace(/^\$\{([^']+)\}/, '$1')
    : id;

export const wrapFieldId = id => '${' + unwrapFieldId(id) + '}';

// escape string for RegExp search - https://stackoverflow.com/a/3561711/
const escapePattern = str => str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

export const ilikeToRegExp = pattern => {
  /* eslint-disable no-control-regex */
  // ASCII control characters as placeholders for escaped ILIKE wildcards (\%, \_)
  const placeholdEscapes = pattern
    .replace(/\\_/g, '\x00')
    .replace(/\\%/g, '\x01');

  const newPattern = `^${escapePattern(placeholdEscapes)}$`
    // use `[\s\S]` instead of `.` to include linebreaks
    .replace(/%/g, '[\\s\\S]*')
    .replace(/_/g, '[\\s\\S]')
    // restore ILIKE escapes
    .replace(/\x00/g, '_')
    .replace(/\x01/g, '%');

  return new RegExp(newPattern, 'i');
};
